import { Injectable } from '@angular/core';
import { StateService, TransitionService as UIRouterTransitionService, Transition } from '@uirouter/angular';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';

import { UiService } from '../../../core/services/core.ui.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';
import { WikiEditService, WikiScope, WikiSavePayload } from './wiki.edit.service';
import { WikiMarkdownEditorComponent } from '../edit-markdown/wiki.markdown-editor.component';
import { WikiDiscardConfirmationModalComponent } from '../discard-confirmation-modal/wiki.discard-confirmation-modal.component';
import { WikiConvertOnSaveModalComponent } from '../convert-on-save-modal/wiki.convert-on-save-modal.component';
import { WikiConstants, WikiContentFormat } from '../../../plugins/wiki/wiki.constants';
import { WikiConflictService } from './wiki.conflict.service';

/** Route configuration that varies per consumer */
export interface WikiEditLifecycleConfig {
    /** The UI-Router state to match in the onBefore transition hook */
    fromState?: string;
    /** The state to reload after saving */
    reloadState?: string;
    /** The state to navigate to when the user declines conversion or loading fails */
    dismissState?: string;
    /** Params to pass when navigating to dismissState */
    dismissParams?: any;
    /**
     * When `false`, no UI-Router transition hook is registered and navigation is
     * left entirely to the host (which drives save via {@link flushPendingSave} on
     * tab-switch / destroy). Defaults to `true` for the standalone routed editor.
     */
    routeHook?: boolean;
}

/** State exposed by the lifecycle service for template bindings */
export interface WikiEditLifecycleState {
    content_type: WikiContentFormat;
    originalContentType: WikiContentFormat;
    isConverting: boolean;
    wasConvertedFromHtml: boolean;
    convertedMarkdownContent: string;
    editableContent: string;
    contentOriginal: string;
    contentReady: boolean;
    pageName: string;
    wikiId: number;
}

/**
 * Manages the shared wiki-edit lifecycle:
 *  - Content loading (raw markdown or HTML→markdown conversion)
 *  - Conversion modal display
 *  - Change detection
 *  - Auto-save on navigation (transition hook)
 *  - Save / save-confirmation
 *
 * Each consumer creates an instance via `createInstance()` so that
 * multiple editors on screen don't share mutable state.
 */
@Injectable()
export class WikiEditLifecycleService {
    constructor(
        private stateService: StateService,
        private $transitions: UIRouterTransitionService,
        private modalService: NgbModal,
        private uiService: UiService,
        private gfTranslateService: GfTranslateService,
        private wikiEditService: WikiEditService,
        private wikiConflictService: WikiConflictService,
    ) {}

    createInstance(config: WikiEditLifecycleConfig): WikiEditLifecycleInstance {
        return new WikiEditLifecycleInstance(
            config,
            this.stateService,
            this.$transitions,
            this.modalService,
            this.uiService,
            this.gfTranslateService,
            this.wikiEditService,
            this.wikiConflictService,
        );
    }
}

/**
 * Per-component instance that holds mutable edit state.
 * Components delegate lifecycle methods to this object.
 */
export class WikiEditLifecycleInstance implements WikiEditLifecycleState {
    content_type: WikiContentFormat = WikiConstants.ContentFormat.HTML;
    originalContentType: WikiContentFormat = WikiConstants.ContentFormat.HTML;
    isConverting: boolean = false;
    wasConvertedFromHtml: boolean = false;
    convertedMarkdownContent: string = '';
    editableContent: string = '';
    contentReady: boolean = false;
    pageName: string = '';
    wikiId: number = 0;

    contentOriginal: string = '';

    /**
     * The page version this editor loaded, sent as `baseVersion` on every save so the server can
     * reject the write if someone else saved in the meantime. 0 means "unknown" (a brand-new
     * page, or a load that predates the server reporting it), which skips the check.
     *
     * Re-read from the save response after every successful save — otherwise the second save in
     * one editing session would conflict with the user's own first save.
     */
    baseVersion: number = 0;

    private pendingTransition: Transition | null = null;
    private transitionHook: any = null;
    private beforeLeaveHooks: Array<(transition: Transition) => Promise<any> | void> = [];

    /** Conversion quality score 0.0–1.0, shown in the inline alert */
    conversionQuality: number = 0;
    /** Original HTML before conversion, shown in the inline side-by-side preview */
    conversionOriginalHtml: string = '';
    /** Warnings from the conversion validator */
    conversionWarnings: string[] = [];
    /** Whether the inline conversion alert has been dismissed by the user */
    conversionAlertDismissed: boolean = false;

    /** Set after any save so the next navigation forces a state reload */
    private needsReloadOnLeave: boolean = false;

    /**
     * Whether content has been persisted since the host last refetched the page, clearing the flag.
     *
     * Every successful save sets it.
     */
    consumeSavedSinceLoad(): boolean {
        const saved = this.needsReloadOnLeave;
        this.needsReloadOnLeave = false;
        return saved;
    }

    /** Called when a brand-new page is first saved and receives its real ID. Supports multiple
     *  callbacks; a callback may return a Promise, which the save flow awaits before navigating. */
    private afterSaveHooks: Array<(newId: number) => Promise<any> | void> = [];

    /** Extra fields merged into the save payload just before each save */
    private savePayloadExtra: any = {};

    /** Reference to the component's @ViewChild editor — set by the component */
    private editorRef: () => WikiMarkdownEditorComponent | null | undefined = () => null;

    /** Reference to wikiPage input — set by the component */
    private wikiPageRef: () => any = () => null;

    /** Resolves the save scope (project unix-name, or section/refId) — set by the component */
    private scopeRef: () => WikiScope = () => ({ section: 'project', refId: 0 });

    constructor(
        private config: WikiEditLifecycleConfig,
        private stateService: StateService,
        private $transitions: UIRouterTransitionService,
        private modalService: NgbModal,
        private uiService: UiService,
        private gfTranslateService: GfTranslateService,
        private wikiEditService: WikiEditService,
        private wikiConflictService: WikiConflictService,
    ) {}

    // ─────────────────── setup ────────────────────

    /**
     * Bind component references so the lifecycle can read ViewChild and Inputs.
     * Call this in the component constructor or before init().
     */
    setRefs(
        editorRef: () => WikiMarkdownEditorComponent | null | undefined,
        wikiPageRef: () => any,
        scopeRef: () => WikiScope,
    ): void {
        this.editorRef = editorRef;
        this.wikiPageRef = wikiPageRef;
        this.scopeRef = scopeRef;
    }

    /** Update dismissParams after construction (e.g. when pageName is only known at ngOnInit time). */
    setDismissParams(params: any): void {
        this.config.dismissParams = params;
    }

    /** Register a hook that runs before the built-in onBeforeLeave logic (e.g. saving tags).
     *  If the hook returns a Promise, onBeforeLeave awaits it before navigating. */
    registerBeforeLeaveHook(fn: (transition: Transition) => Promise<any> | void): void {
        this.beforeLeaveHooks.push(fn);
    }

    /** Register a callback fired when a new page is first saved and receives its ID. Multiple
     *  hooks are supported, and a hook may return a Promise that the save flow awaits. */
    registerAfterSaveHook(fn: (newId: number) => Promise<any> | void): void {
        this.afterSaveHooks.push(fn);
    }

    /** Merge extra fields into the next save payload (e.g. attachmentIds). */
    setSavePayloadExtra(extra: any): void {
        this.savePayloadExtra = extra || {};
    }

    /**
     * Initialize lifecycle: detect format, register transition hook, load content.
     * Call from ngOnInit after setting pageName / wikiId / wikiPage / project.
     */
    init(pageName: string, wikiPage: any): void {
        this.pageName = pageName;
        this.wikiId = _.get(wikiPage, 'id', 0);
        this.contentOriginal = _.get(wikiPage, 'rel.content.data', '');
        // Provisional — the content load below refetches and reports the version it actually
        // served, which is the authoritative base for this editing session.
        this.baseVersion = _.get(wikiPage, 'rel.content.version', 0)
            || _.get(wikiPage, 'latestVersion', 0);

        this.content_type = _.get(wikiPage, 'rel.content.content_type', WikiConstants.ContentFormat.HTML);
        this.originalContentType = this.content_type;

        if (this.config.routeHook !== false && this.config.fromState) {
            this.transitionHook = this.$transitions.onBefore(
                { exiting: this.config.fromState },
                (transition: Transition) => this.onBeforeLeave(transition)
            );
        }

        if (this.wikiId > 0) {
            if (this.originalContentType === WikiConstants.ContentFormat.HTML) {
                this.autoConvertHtmlToMarkdown();
            } else {
                this.loadRawMarkdownContent();
            }
        } else {
            this.contentOriginal = '';
            this.editableContent = '';
            this.content_type = WikiConstants.ContentFormat.MARKDOWN;
            this.originalContentType = WikiConstants.ContentFormat.MARKDOWN;
            this.baseVersion = 0;
            this.contentReady = true;
        }
    }

    /**
     * Tear down: deregister transition hook and close any open modal.
     * Call from ngOnDestroy.
     */
    destroy(): void {
        if (this.transitionHook) {
            this.transitionHook();
            this.transitionHook = null;
        }
    }

    // ─────────────────── transition hook ────────────────────

    onBeforeLeave(transition: Transition): boolean | Promise<any> {
        // Hooks may save tags asynchronously; collect any Promises they return so we can wait
        // on them (and surface a single success/failure) before navigating away.
        const hookPromises: Promise<any>[] = this.beforeLeaveHooks
            .map(hook => hook(transition))
            .filter((result): result is Promise<any> => result instanceof Promise);

        const needsReload = this.needsReloadOnLeave;
        this.needsReloadOnLeave = false;

        const targetReloaded = () => this.stateService.target(
            transition.to().name!,
            transition.params('to'),
            { reload: this.config.reloadState }
        );

        if (this.wasConvertedFromHtml && this.hasContentChanges()) {
            return this.showConvertOnSaveModal().then((choice) => {
                if (choice === 'save') {
                    return this.saveMarkdownContent()
                        .then(() => targetReloaded())
                        .catch(() => false);
                }
                if (choice === 'discard') {
                    // Leave without saving; page stays HTML in DB.
                    this.wasConvertedFromHtml = false;
                    return true;
                }
                // 'cancel' → stay on the edit view
                return false;
            });
        }

        const contentChanged = this.content_type === WikiConstants.ContentFormat.MARKDOWN && this.hasContentChanges();
        const allPromises: Promise<any>[] = [...hookPromises];

        if (contentChanged) {
            allPromises.push(this.saveMarkdownContentSilently());
        }

        if (allPromises.length > 0) {
            return Promise.all(allPromises)
                .then(() => {
                    this.uiService.message.showSuccess(
                        this.gfTranslateService.translate('Changes saved'),
                        this.gfTranslateService.translate('Wiki')
                    );
                    return targetReloaded();
                })
                .catch(() => false);
        }

        // ── Page was already saved during this session: force reload on leave ──
        if (needsReload) {
            return Promise.resolve(targetReloaded());
        }

        return true;
    }

    private showConvertOnSaveModal(): Promise<'save' | 'discard' | 'cancel'> {
        const modalRef: NgbModalRef = this.modalService.open(WikiConvertOnSaveModalComponent, {
            backdrop: 'static',
            keyboard: false,
        });

        return modalRef.result.then(
            (result) => (result === 'save' || result === 'discard') ? result : 'cancel',
            () => 'cancel' as const,
        );
    }

    // ─────────────────── content loading ────────────────────

    private autoConvertHtmlToMarkdown(): void {
        const originalHtml = _.get(this.wikiPageRef(), 'rel.content.data', '');

        if (!originalHtml || !originalHtml.trim()) {
            this.contentOriginal = '';
            this.editableContent = '';
            this.content_type = WikiConstants.ContentFormat.MARKDOWN;
            this.originalContentType = WikiConstants.ContentFormat.MARKDOWN;
            this.contentReady = true;
            return;
        }

        this.isConverting = true;
        this.wikiEditService.convertHtmlToMarkdown(this.wikiId)
            .then((result) => {
                this.isConverting = false;
                this.convertedMarkdownContent = result.editableContent;
                this.editableContent = result.editableContent;
                this.content_type = result.content_type || WikiConstants.ContentFormat.MARKDOWN;
                this.wasConvertedFromHtml = true;
                this.conversionQuality = result.conversionQuality ?? 0;
                this.conversionOriginalHtml = result.originalHtml || originalHtml;
                this.conversionWarnings = result.conversionWarnings || [];
                this.baseVersion = result.version || this.baseVersion;
                this.contentReady = true;
            })
            .catch((error: any) => {
                console.error('Auto-conversion failed:', error);
                this.isConverting = false;
                // Fallback: let the user edit the raw content anyway
                this.editableContent = originalHtml;
                this.content_type = WikiConstants.ContentFormat.HTML;
                this.contentReady = true;
            });
    }

    private loadRawMarkdownContent(): void {
        this.isConverting = true;
        this.wikiEditService.loadRawMarkdownContent(this.wikiId)
            .then((result) => {
                this.editableContent = result.editableContent;
                this.contentOriginal = result.editableContent;
                this.baseVersion = result.version || this.baseVersion;
                this.isConverting = false;
                this.contentReady = true;
            })
            .catch((error: any) => {
                console.error('Failed to load editable content:', error);
                this.uiService.message.showError(
                    this.gfTranslateService.translate('Failed to load wiki content for editing.')
                );
                if (this.config.dismissState) {
                    this.stateService.go(this.config.dismissState, this.config.dismissParams || {});
                }
            });
    }

    // ─────────────────── content change detection ────────────────────

    hasContentChanges(): boolean {
        const currentContent = this.wikiEditService.getEditorMarkdown(this.editorRef());
        if (currentContent === null) return false;
        // Trim trailing whitespace to avoid false positives from editor normalization
        const normalizedCurrent = currentContent.replace(/\s+$/, '');
        if (this.wasConvertedFromHtml) {
            return normalizedCurrent !== this.convertedMarkdownContent.replace(/\s+$/, '');
        }
        return normalizedCurrent !== this.contentOriginal.replace(/\s+$/, '');
    }

    private getCurrentMarkdown(): string | null {
        return this.wikiEditService.getEditorMarkdown(this.editorRef());
    }

    // ─────────────────── save logic ────────────────────

    /**
     * Rejection marker used when a save hit a conflict and the user chose to keep editing.
     * Callers treat it as "stay put, say nothing more" — the conflict modal has already
     * explained the situation, so a second error toast would just be noise.
     */
    static readonly CONFLICT_UNRESOLVED = 'wiki-conflict-unresolved';

    /** True when a rejection came from {@link postWithConflictHandling} rather than a real failure. */
    private isUnresolvedConflict(error: any): boolean {
        return error === WikiEditLifecycleInstance.CONFLICT_UNRESOLVED;
    }

    /**
     * POST a save, routing a 409 through the conflict modal.
     *
     * Resolves with the server response when the save lands (either first time, or after the
     * user chose to overwrite). Rejects with {@link CONFLICT_UNRESOLVED} when the user chose to
     * keep editing, and with the original error for any other failure.
     */
    private postWithConflictHandling(payload: WikiSavePayload): Promise<any> {
        return this.wikiEditService.saveWikiContent(this.wikiId, payload)
            .catch((error) => {
                const conflict = this.wikiConflictService.parse(error);
                if (!conflict) { return Promise.reject(error); }

                return this.wikiConflictService.open(conflict, this.wikiId).then((choice) => {
                    if (choice !== 'overwrite') {
                        return Promise.reject(WikiEditLifecycleInstance.CONFLICT_UNRESOLVED);
                    }
                    // Deliberate last-write-wins: rebase onto the version that beat us and save
                    // again. Nothing is lost — the other edit stays in the page history.
                    return this.wikiEditService.saveWikiContent(this.wikiId, {
                        ...payload,
                        baseVersion: conflict.currentVersion,
                    });
                });
            });
    }

    /**
     * Move our base forward to the version the server just wrote, so a second save in the same
     * editing session isn't rejected as a conflict with our own first save.
     */
    private adoptSavedVersion(response: any): void {
        const saved = _.get(response, 'latestVersion', 0);
        if (saved > 0) { this.baseVersion = saved; }
    }

    private performSave(content: string, navigate: boolean): Promise<void> {
        const payload = { ...this.wikiEditService.buildSavePayload(content, this.pageName, this.scopeRef(), WikiConstants.ContentFormat.MARKDOWN, this.baseVersion), ...this.savePayloadExtra };
        this.savePayloadExtra = {};
        return this.postWithConflictHandling(payload)
            .then((response: any) => {
                this.adoptSavedVersion(response);
                this.uiService.message.showSuccess(
                    this.gfTranslateService.translate('Wiki page saved as Markdown successfully')
                );
                this.contentOriginal = content;
                this.convertedMarkdownContent = content;
                this.wasConvertedFromHtml = false;
                this.needsReloadOnLeave = true;

                if (navigate && this.pendingTransition) {
                    this.stateService.go(
                        this.pendingTransition.to().name,
                        this.pendingTransition.params('to'),
                        { reload: this.config.reloadState }
                    );
                    this.pendingTransition = null;
                }
            })
            .catch((error) => {
                if (this.isUnresolvedConflict(error)) { return Promise.reject(error); }
                console.error('Failed to save wiki page:', error);
                this.uiService.message.showError(
                    this.gfTranslateService.translate('Failed to save wiki page')
                );
                return Promise.reject(error);
            });
    }

    private saveMarkdownContentCore(content: string, showNotification: boolean): Promise<void> {
        const payload = { ...this.wikiEditService.buildSavePayload(content, this.pageName, this.scopeRef(), WikiConstants.ContentFormat.MARKDOWN, this.baseVersion), ...this.savePayloadExtra };
        this.savePayloadExtra = {};
        return this.postWithConflictHandling(payload)
            .catch((response) => {
                // A conflict the user declined to overwrite has already been explained by the
                // modal — propagate it silently so callers stay put without a second toast.
                if (this.isUnresolvedConflict(response)) { return Promise.reject(response); }
                // Surface only genuine page-save failures here — keep this separate from any
                // afterSave hook failure (e.g. tag save) below, which reports its own error.
                // Prefer the server's own reason when it gave one: a create refused because the
                // page name is taken is actionable, where the status text is not.
                const reason = this.wikiConflictService.parseRejectionMessage(response)
                    || response.statusText;
                this.uiService.message.showError('An error occurred while saving the wiki page: ' + reason);
                return Promise.reject(response);
            })
            .then((response: any) => {
                const wasNew = this.wikiId === 0;
                let hookResult: Promise<any> = Promise.resolve();
                if (wasNew && response?.id) {
                    this.wikiId = response.id;
                    const wp = this.wikiPageRef();
                    if (wp) _.set(wp, 'id', response.id);
                    // Run afterSave hooks (e.g. saving tags with the real ID) and await any
                    // Promises they return so callers don't navigate/reload before they finish.
                    const results = this.afterSaveHooks
                        .map(hook => hook(this.wikiId))
                        .filter((r): r is Promise<any> => r instanceof Promise);
                    hookResult = Promise.all(results);
                }
                // The page itself saved successfully — record that regardless of hook outcome.
                this.adoptSavedVersion(response);
                this.contentOriginal = content;
                this.convertedMarkdownContent = content;
                this.wasConvertedFromHtml = false;
                this.needsReloadOnLeave = true;
                if (showNotification) {
                    this.uiService.message.showSuccess('Wiki page saved successfully');
                }
                return hookResult.then(() => {});
            });
    }

    saveMarkdownContent(contentOverride?: string): Promise<void> {
        const content = contentOverride ?? this.getCurrentMarkdown();
        if (content === null) {
            return Promise.reject(new Error('Editor not ready'));
        }
        return this.saveMarkdownContentCore(content, true);
    }

    /** Like saveMarkdownContent but suppresses the success notification (onBeforeLeave shows its own). */
    private saveMarkdownContentSilently(contentOverride?: string): Promise<void> {
        const content = contentOverride ?? this.getCurrentMarkdown();
        if (content === null) {
            return Promise.reject(new Error('Editor not ready'));
        }
        return this.saveMarkdownContentCore(content, false);
    }

    // ─────────────────── event handlers ────────────────────

    onMarkdownSave(content: string): void {
        if (this.wasConvertedFromHtml) {
            this.performSave(content, false);
        } else {
            this.saveMarkdownContent(content);
        }
    }

    /**
     * Persist any pending edits before leaving the Edit surface — used by embedded
     * hosts (e.g. the wiki workspace) that switch tabs instead of navigating routes.
     * Mirrors {@link onBeforeLeave} but performs no router navigation.
     *
     * Resolves to `true` when it is safe to leave the editor, `false` when the user
     * cancelled the HTML→Markdown convert-on-save prompt and should stay editing.
     */
    flushPendingSave(): Promise<boolean> {
        if (this.wasConvertedFromHtml && this.hasContentChanges()) {
            return this.showConvertOnSaveModal().then((choice) => {
                if (choice === 'save') {
                    return this.runLeaveHooksAndSave().then(() => true, () => false);
                }
                if (choice === 'discard') {
                    this.wasConvertedFromHtml = false;
                    return true;
                }
                return false; // 'cancel' → stay on the Edit tab
            });
        }

        return this.runLeaveHooksAndSave().then(() => true, () => false);
    }

    /**
     * Run the registered before-leave hooks (claim staged attachments, save tags) bundled with the
     * content save — the same work {@link onBeforeLeave} does, minus router navigation. Embedded
     * hosts that switch tabs instead of navigating routes (e.g. `<wiki-container>`) must flush
     * through here; calling {@link saveMarkdownContent} alone skips the hooks, leaving uploaded
     * attachments unclaimed.
     */
    private runLeaveHooksAndSave(): Promise<any> {
        const hookPromises: Promise<any>[] = this.beforeLeaveHooks
            .map(hook => hook(undefined as any))
            .filter((result): result is Promise<any> => result instanceof Promise);

        const contentChanged = this.content_type === WikiConstants.ContentFormat.MARKDOWN && this.hasContentChanges();
        const allPromises: Promise<any>[] = [...hookPromises];
        if (contentChanged) {
            allPromises.push(this.saveMarkdownContentSilently());
        }

        if (allPromises.length === 0) {
            return Promise.resolve();
        }
        return Promise.all(allPromises).then(() => {
            this.uiService.message.showSuccess(
                this.gfTranslateService.translate('Changes saved'),
                this.gfTranslateService.translate('Wiki')
            );
        });
    }

    /**
     * Cancel editing: if there are unsaved changes, show a discard confirmation modal.
     * On confirm, deregister hook and navigate away.
     */
    cancelEditing(): void {
        const hasChanges = this.hasContentChanges();

        if (!hasChanges) {
            // No changes — navigate away immediately
            if (this.transitionHook) {
                this.transitionHook();
                this.transitionHook = null;
            }
            this.stateService.go(this.config.dismissState, this.config.dismissParams || {});
            return;
        }

        const modalRef: NgbModalRef = this.modalService.open(WikiDiscardConfirmationModalComponent, {
            backdrop: 'static',
            keyboard: false,
        });

        modalRef.componentInstance.confirm.subscribe(() => {
            // Deregister hook to prevent auto-save on navigation
            if (this.transitionHook) {
                this.transitionHook();
                this.transitionHook = null;
            }
            this.wasConvertedFromHtml = false;
            this.stateService.go(this.config.dismissState, this.config.dismissParams || {});
        });

        // cancel subscriber is a no-op — modal closes itself via activeModal.close()
    }
}
